import { DocumentFilter } from "iconsax-reactjs"
import { ArrowLeft, Search } from "lucide-react"
import { useMemo, useState } from "react"
import InfiniteScroll from "react-infinite-scroll-component"

import { LoadingSpinner } from "@/components/shared"
import { getAffiliateExplore } from "@/features/jobs/affiliates/api/affiliate.api"
import { Filter } from "@/features/jobs/affiliates/components/affiliate-filter"
import { AffiliateItem } from "@/features/jobs/affiliates/components/affiliate-item"
import { CampaignLoadingSkeleton as CampaignItemSkeleton } from "@/features/jobs/explore/components/campaign/campaign-ui"
import { useDebounce, useInfiniteScrollQuery } from "@/modules/helpers/hooks"
import { transformFilterToQueryParams, useFilterStore } from "@/modules/helpers/hooks/shared"
import { sendHaptic } from "@/modules/helpers/utils"
import { Affiliate } from "@/modules/types"

const AffiliateSearchPage = () => {
  const [search, setSearch] = useState("")
  const debouncedSearch = useDebounce(search, 300)
  const { filterState, countFilter } = useFilterStore()
  const [isVisible, setIsVisible] = useState(false)

  // Transform filter state to API query params
  const apiParams = useMemo(() => {
    const transformed = transformFilterToQueryParams(filterState)
    return {
      search: debouncedSearch || undefined,
      ...transformed
    }
  }, [filterState, debouncedSearch])

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage, forceRefetch } =
    useInfiniteScrollQuery<unknown, Affiliate>({
      queryKey: ["affiliate-search", debouncedSearch, JSON.stringify(filterState)],
      queryFn: getAffiliateExplore,
      params: apiParams
    })

  const campaigns = data?.list || []

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header Search */}
      <div className="sticky top-0 z-50 flex items-center gap-2 p-4 border-b border-gray-200 bg-white">
        <button
          onClick={() => {
            window.history.back()
            sendHaptic()
          }}
          className="p-2 hover:bg-gray-100 rounded-full bg-primary-50"
        >
          <ArrowLeft className="w-5 h-5 text-primary" />
        </button>
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Tìm kiếm sản phẩm..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-xl border border-gray-300 bg-white py-3 pl-10 pr-4 text-sm focus:border-gray-800 focus:outline-none focus:ring-1 focus:ring-gray-800"
            autoFocus
          />
        </div>
        <div
          className="rounded-xl w-12 h-12 border flex justify-center items-center bg-white relative cursor-pointer"
          onClick={() => {
            setIsVisible(true)
            sendHaptic()
          }}
        >
          {countFilter > 0 && (
            <div className="bg-red-500 text-white flex justify-center items-center text-[10px] font-medium w-4 h-4 rounded-full absolute -top-1 -right-1">
              {countFilter}
            </div>
          )}
          <DocumentFilter size={24} className="text-primary-800" />
        </div>
      </div>
      <Filter refetch={forceRefetch} isVisible={isVisible} setIsVisible={setIsVisible} />
      {/* Content */}
      <div className="flex-1 overflow-y-auto" id="scrollable">
        {isLoading && (
          <div className="grid grid-cols-2 gap-2 p-4">
            {[...Array(6)].map((_, i) => (
              <CampaignItemSkeleton key={i} />
            ))}
          </div>
        )}

        {!isLoading && campaigns.length === 0 && (
          <div className="h-40 flex items-center justify-center">
            <p className="text-gray-400 text-sm">Không tìm thấy sản phẩm nào.</p>
          </div>
        )}

        <InfiniteScroll
          dataLength={campaigns.length}
          next={() => !isFetchingNextPage && hasNextPage && fetchNextPage()}
          hasMore={hasNextPage}
          loader={
            <div className="flex justify-center py-4">
              <LoadingSpinner className="w-20 h-20" />
            </div>
          }
          scrollableTarget="scrollable"
        >
          <div className="grid grid-cols-2 gap-2 p-4">
            {campaigns.map((item) => (
              <AffiliateItem key={item.id} data={item} />
            ))}
          </div>
        </InfiniteScroll>
      </div>
    </div>
  )
}

export default AffiliateSearchPage
